<template>
	<div class="ach-form-dialog">
		<header class="ach-form-heading">
			<span class="ach-form-icon" aria-hidden="true">
				<portal-icon name="landmark" :size="21"></portal-icon>
			</span>
			<h2>{{ $t('invoices.manual-ach.account-info') }}</h2>
			<button type="button" class="dialog-close" :aria-label="$t('global.close')" @click="handleClose">
				<portal-icon name="x" :size="19"></portal-icon>
			</button>
		</header>

		<div class="ach-form-body" v-if="stripeAccountId">
			<v-form ref="accountForm" class="ach-field-grid" @submit.prevent v-model="isValid">
				<v-text-field
					v-model="accountHolderName"
					:label="$t('invoices.manual-ach.name')"
					hide-details
					:rules="noBlank"
					persistent-placeholder
					class="portal-form-field field-wide"
				></v-text-field>
				<v-select
					v-model="accountHolderType"
					:items="holderTypes"
					:label="$t('invoices.manual-ach.type')"
					hide-details
					persistent-placeholder
					class="portal-form-field"
				></v-select>
				<v-select
					v-model="accountType"
					:items="accountTypes"
					:label="$t('invoices.manual-ach.account-type')"
					hide-details
					persistent-placeholder
					class="portal-form-field"
				></v-select>
				<v-text-field
					v-model="routingNumber"
					:label="$t('invoices.manual-ach.routing-number')"
					hide-details
					maxlength="9"
					:rules="requiredRules"
					persistent-placeholder
					class="portal-form-field"
				></v-text-field>
				<v-text-field
					v-model="accountNumber"
					:label="$t('invoices.manual-ach.account-number')"
					hide-details
					:rules="requiredRules"
					persistent-placeholder
					class="portal-form-field"
				></v-text-field>
			</v-form>
		</div>
		<div v-else class="ach-form-loading">
			<v-progress-circular indeterminate :size="32" :width="3" color="primary"></v-progress-circular>
		</div>

		<footer class="ach-form-footer">
			<button type="button" class="dialog-action outline" @click="handleClose">
				{{ $t('global.cancel') }}
			</button>
			<button type="button" class="dialog-action primary" @click="linkNewAccount" :disabled="!isValid">
				{{ $t('invoices.payment.new-linked') }}
			</button>
		</footer>
	</div>
</template>

<script>
	import PaymentsService from '@/modules/invoices/PaymentsService';
	import ConfirmModal from '@/components/ConfirmModal';
	import PortalIcon from '@/components/PortalIcon.vue';

	export default {
		name: 'ManualAchAccount',

		props: [''],

		components: { PortalIcon },

		data: function() {
			return {
				accountHolderName: null,
				accountNumber: null,
				routingNumber: null,
				accountHolderType: 'individual',
				accountType: 'checking',
				paymentsService: new PaymentsService(),
				stripeAccountId: null,
				isValid: false,

				accountTypes: [
					{ text: this.$t('invoices.manual-ach.checking'), value: 'checking' },
					{ text: this.$t('invoices.manual-ach.savings'), value: 'savings' },
				],

				holderTypes: [
					{ text: this.$t('invoices.manual-ach.individual'), value: 'individual' },
					{ text: this.$t('invoices.manual-ach.company'), value: 'company' },
				],

        noBlank: [
          (v) => !!v || 'Required'
        ],

				requiredRules: [
          (v) => !!v || 'Required',
          (v) => v && v.length >= 5 || 'Required',
        ],
			};
		},

		mounted() {
			this.getStripeAccountId();
		},

		beforeDestroy() {},

		methods: {
			getStripeAccountId: function() {
				this.paymentsService.getStripeAccountId().then((res) => {
					this.stripeAccountId = res.data;
				});
			},

			linkNewAccount: function() {
        if(!this.$refs.accountForm.validate()){
          return;
        }

				let stripe = window.Stripe(this.$store.state.stripeKey, {
					stripeAccount: this.stripeAccountId,
				});

        this.$store.commit('startLoading');

				stripe
					.createToken('bank_account', {
						country: 'US',
						currency: 'usd',
						routing_number: this.routingNumber.replace(/\D/g, ''),
						account_number: this.accountNumber.replace(/\D/g, ''),
						account_holder_name: this.accountHolderName,
						account_holder_type: this.accountHolderType,
					})
					.then((res) => {
            if(res.token){
              this.paymentsService.addBankAccount(res.token.id).then((res) => {
                this.$store.commit('stopLoading');
                this.$emit('result',res.data);
              }).catch((err) => {
                this.handleError(err.response.data.message)
              })
            }else if(res.error){
              this.handleError(res.error.message);
            }else{
              this.handleError('There was an unknown error.');
            }
					})
					.catch((err) => {
						this.handleError(err.error.message);
					});
			},

			handleError: function(message) {
        this.$store.commit('stopLoading');

				let binding = {
					headingText: this.$t('error.connecting-account'),
					bodyText: message,
					hideNo: true,
					yesText: this.$t('global.okay'),
				};
				this.$store.state.globalModalController.openModal(ConfirmModal, binding, true, false);
			},

			handleClose() {
				this.$emit('result', false);
			},
		},

		computed: {},
	};
</script>

<style scoped lang="scss">
	.ach-form-dialog {
		width: min(580px, calc(100vw - 32px));
		max-height: calc(100vh - 32px);
		display: flex;
		flex-direction: column;
		overflow: hidden;
		border-radius: var(--brand-surface-radius, 18px);
		background: var(--portal-surface);
	}

	.ach-form-heading {
		min-height: 68px;
		display: flex;
		align-items: center;
		gap: 11px;
		padding: 14px 18px;
		border-bottom: 1px solid var(--portal-border);
	}

	.ach-form-heading h2 {
		min-width: 0;
		flex: 1;
		margin: 0;
		color: var(--portal-text);
		font-size: 17px;
		font-weight: 650;
		text-align: left;
	}

	.ach-form-icon,
	.dialog-close {
		display: grid;
		place-items: center;
		border-radius: 11px;
	}

	.ach-form-icon {
		width: 38px;
		height: 38px;
		background: color-mix(in srgb, var(--v-primary-base) 9%, var(--portal-surface));
		color: var(--v-primary-base);
	}

	.dialog-close {
		width: 36px;
		height: 36px;
		border: 0;
		background: transparent;
		color: var(--portal-muted);
		cursor: pointer;
	}

	.ach-form-body {
		overflow: auto;
		padding: 20px;
	}

	.ach-field-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 14px;
	}

	.field-wide {
		grid-column: 1 / -1;
	}

	.portal-form-field {
		margin: 0;
		padding: 0;
	}

	.portal-form-field ::v-deep .v-input__slot {
		min-height: 52px;
		margin: 0;
		padding: 0 13px !important;
		border: 1px solid var(--portal-border) !important;
		border-radius: var(--brand-field-radius, 11px) !important;
		background: var(--portal-surface) !important;
		box-shadow: none !important;
	}

	.portal-form-field ::v-deep .v-input__slot::before,
	.portal-form-field ::v-deep .v-input__slot::after {
		display: none !important;
	}

	.ach-form-loading {
		min-height: 220px;
		display: grid;
		place-items: center;
	}

	.ach-form-footer {
		display: flex;
		justify-content: flex-end;
		gap: 9px;
		padding: 14px 18px;
		border-top: 1px solid var(--portal-border);
	}

	.dialog-action {
		min-height: 42px;
		padding: 0 16px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-button-radius, 11px);
		font: inherit;
		font-size: 13px;
		font-weight: 650;
		cursor: pointer;
	}

	.dialog-action.outline {
		background: var(--portal-surface);
		color: var(--portal-text);
	}

	.dialog-action.primary {
		border-color: var(--v-primary-base);
		background: var(--v-primary-base);
		color: var(--v-white-base);
	}

	.dialog-action:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}

	@media (max-width: 599px) {
		.ach-field-grid {
			grid-template-columns: 1fr;
		}

		.field-wide {
			grid-column: auto;
		}

		.ach-form-footer {
			display: grid;
			grid-template-columns: 1fr 1fr;
		}
	}
</style>
